import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {toast} from 'react-toastify';

import {merchantService} from '@/services/merchants';
import {AfriexCurrencyCode} from '@/types';
import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import type {AfriexSecurityNote} from '@/types/Notes';
import {getErrorMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';

import type {BusinessWalletBalances} from './types';

const SECURITY_NOTES_LIMIT = 6;

export const useBusinessDetails = () => {
  const {businessId} = useParams();

  const [businessDetails, setBusinessDetails] = useState<AfriexBusiness | null>(
    null,
  );
  const [businessUser, setBusinessUser] = useState<AfriexBusinessUser | null>(
    null,
  );
  const [businessWallet, setBusinessWallet] = useState<BusinessWalletBalances>(
    {},
  );
  const [isBusinessWalletLoading, setIsBusinessWalletLoading] = useState(false);
  const [loadingBusinessDetails, setIsLoadingBusinessDetails] = useState(false);
  const [isTransferringOwner, setIsTransferringOwner] = useState(false);
  const [isMovingMember, setIsMovingMember] = useState(false);
  const [isRetiringOrg, setIsRetiringOrg] = useState(false);
  const [businessMembers, setBusinessMembers] = useState<AfriexBusinessUser[]>(
    [],
  );
  const [isLoadingBusinessMembers, setIsLoadingBusinessMembers] =
    useState(false);
  const [currencies, _] = useState<AfriexCurrencyCode[]>([
    ...Object.values(AfriexCurrencyCode),
  ]);
  const [securityNotes, setSecurityNotes] = useState<AfriexSecurityNote[]>([]);
  const [isLoadingSecurityNotes, setIsLoadingSecurityNotes] = useState(false);
  const [securityNotesPage, setSecurityNotesPage] = useState(0);
  const [hasMoreNotes, setHasMoreNotes] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const fetchBusinessWallet = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        if (!businessId || !currencies) {
          return;
        }
        setIsBusinessWalletLoading(true);
        const walletResponse = await merchantService.getBusinessWallet(
          businessId,
          currencies,
          signal,
        );
        // Always overwrite (with {} when empty) so a previously viewed
        // business's balances can never linger when switching businesses.
        // Drop only zero balances, per wallet type (Payout/Collection), since
        // they're keyed by wallet type first. Zeroes are noise across a long
        // currency list; negatives are debt a post-funding business actually
        // owes, and hiding those is what this screen is for showing.
        const nonZeroBalances: BusinessWalletBalances = walletResponse
          ? Object.fromEntries(
              Object.entries(walletResponse).map(([walletType, balances]) => [
                walletType,
                Object.fromEntries(
                  Object.entries(balances ?? {}).filter(
                    ([, balance]) => balance !== 0,
                  ),
                ),
              ]),
            )
          : {};
        setBusinessWallet(nonZeroBalances);
      } catch (err) {
        // The request was superseded (businessId changed or the screen
        // unmounted); a newer request now owns the state, so ignore this one.
        if (signal?.aborted) {
          return;
        }
        const errorMessage = getErrorMessage(err, '');
        toast.error('Failed to fetch business wallet:' + errorMessage);
      } finally {
        if (!signal?.aborted) {
          setIsBusinessWalletLoading(false);
        }
      }
    },
    [businessId, currencies],
  );

  const fetchBusinessDetails = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        if (!businessId) {
          return;
        }
        setIsLoadingBusinessDetails(true);
        const response = await merchantService.getBusinessDetails(
          businessId,
          signal,
        );
        setBusinessDetails(response.business);
        setBusinessUser(response.person);
      } catch (err) {
        if (signal?.aborted) {
          return;
        }
        const errorMessage = getErrorMessage(err, '');
        toast.error('Failed to fetch business user details:' + errorMessage);
      } finally {
        if (!signal?.aborted) {
          setIsLoadingBusinessDetails(false);
        }
      }
    },
    [businessId],
  );

  const fetchSecurityNotes = useCallback(
    async (params: {page: number}): Promise<void> => {
      if (!businessId) {
        return;
      }
      const isFirstPage = params.page === 0;
      if (isFirstPage) {
        setIsLoadingSecurityNotes(true);
      } else {
        setIsFetchingMore(true);
      }
      try {
        const result = await merchantService.getBusinessSecurityNotes(
          businessId,
          {page: params.page, limit: SECURITY_NOTES_LIMIT},
        );
        setSecurityNotes((prev) =>
          isFirstPage ? result.data : [...prev, ...result.data],
        );
        setHasMoreNotes(result.data.length >= SECURITY_NOTES_LIMIT);
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to fetch security notes'));
      } finally {
        if (isFirstPage) {
          setIsLoadingSecurityNotes(false);
        } else {
          setIsFetchingMore(false);
        }
      }
    },
    [businessId],
  );

  const loadMoreSecurityNotes = () => setSecurityNotesPage((p) => p + 1);

  const fetchBusinessMembers = useCallback(async (): Promise<void> => {
    if (!businessId) {
      return;
    }
    try {
      setIsLoadingBusinessMembers(true);
      const members = await merchantService.getBusinessMembers(businessId);
      setBusinessMembers(members ?? []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to fetch members'));
    } finally {
      setIsLoadingBusinessMembers(false);
    }
  }, [businessId]);

  const transferOwner = useCallback(
    async (newOwnerUserId: string): Promise<boolean> => {
      if (!businessId || !newOwnerUserId) {
        return false;
      }
      try {
        setIsTransferringOwner(true);
        await merchantService.transferBusinessOwner(businessId, newOwnerUserId);
        toast.success('Organization ownership transferred');
        // The owner badge is drawn from the members list, not the details.
        await Promise.all([fetchBusinessDetails(), fetchBusinessMembers()]);
        return true;
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to transfer ownership'));
        return false;
      } finally {
        setIsTransferringOwner(false);
      }
    },
    [businessId, fetchBusinessDetails, fetchBusinessMembers],
  );

  // Typeahead for picking the target organization to move a member into.
  const searchTargetBusinesses = useCallback(
    async (
      term: string,
    ): Promise<{id: string; name: string; email?: string}[]> => {
      const trimmed = term.trim();
      if (!trimmed) {
        return [];
      }
      try {
        const result =
          await merchantService.searchBusinessAutoComplete(trimmed);
        return (
          (result?.data ?? [])
            .map((entry) => ({
              id: entry.business?.id ?? '',
              name: entry.business?.name ?? '',
              // Owner email — lets the admin tell the main org from a duplicate
              // by its corporate domain.
              email: entry.person?.email,
            }))
            // Never offer the org we're moving out of as a destination.
            .filter((org) => org.id && org.id !== businessId)
        );
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to search organizations'));
        return [];
      }
    },
    [businessId],
  );

  // Duplicate consolidation: request that a member of this org join a
  // confirmed target org instead. This does NOT move anyone directly — it
  // creates an owner-approved invite. The member stays a member here (nothing
  // changes locally) until the target org's owner approves and the member
  // themselves sets a new password to accept.
  const requestMemberMove = useCallback(
    async (userId: string, targetBusinessId: string): Promise<boolean> => {
      if (!userId || !targetBusinessId) {
        return false;
      }
      try {
        setIsMovingMember(true);
        await merchantService.requestMemberMove(userId, targetBusinessId);
        toast.success(
          "Request sent — the target organization's owner must approve it",
        );
        return true;
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to request the move'));
        return false;
      } finally {
        setIsMovingMember(false);
      }
    },
    [],
  );

  // Retire the current (now-empty) org after its members were consolidated
  // elsewhere: deactivates it and removes it from the admin list + search.
  const retireOrg = useCallback(async (): Promise<boolean> => {
    if (!businessId) {
      return false;
    }
    try {
      setIsRetiringOrg(true);
      await merchantService.retireConsolidatedDuplicateOrg(businessId);
      toast.success('Organization deactivated and removed from the list');
      await fetchBusinessDetails();
      return true;
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to retire organization'));
      return false;
    } finally {
      setIsRetiringOrg(false);
    }
  }, [businessId, fetchBusinessDetails]);

  useEffect(() => {
    if (!businessId) {
      return;
    }
    const controller = new AbortController();
    // Each fetcher toasts its own failures, so these handlers only fire if that
    // catch or the finally itself throws. Log rather than swallow silently.
    fetchBusinessDetails(controller.signal).catch(
      logRejection('Failed to fetch business details'),
    );
    fetchBusinessWallet(controller.signal).catch(
      logRejection('Failed to fetch business wallet'),
    );
    // Abort in-flight requests when the businessId changes or the screen
    // unmounts so a stale response can never overwrite the current one.
    return () => controller.abort();
  }, [businessId, fetchBusinessDetails, fetchBusinessWallet]);

  useEffect(() => {
    fetchSecurityNotes({page: securityNotesPage}).catch(
      logRejection('Failed to fetch business security notes'),
    );
  }, [securityNotesPage, fetchSecurityNotes]);

  useEffect(() => {
    fetchBusinessMembers().catch(
      logRejection('Failed to fetch business members'),
    );
  }, [fetchBusinessMembers]);

  return {
    businessDetails,
    businessUser,
    businessWallet,
    isBusinessWalletLoading,
    loadingBusinessDetails,
    securityNotes,
    isLoadingSecurityNotes,
    hasMoreNotes,
    loadMoreSecurityNotes,
    isFetchingMore,
    transferOwner,
    isTransferringOwner,
    businessMembers,
    isLoadingBusinessMembers,
    fetchBusinessMembers,
    requestMemberMove,
    isMovingMember,
    searchTargetBusinesses,
    retireOrg,
    isRetiringOrg,
  };
};
